import React from "react";
import { Box, Button, Paper, Stack } from "@mui/material";
import { Skeleton } from "@mui/lab";
import styled from "styled-components";
import { format, formatDistanceToNow } from "date-fns";
import request from "src/utils/request";
import { getRedirectUrl } from "../upgrade/utils";

const SubscriptionPaper = styled(Paper)`
    border-radius: 14px;
    // min-height: 320px;
`;

const Plan = styled.span`
    font-size: 28px;
    font-weight: 700;
`;

const ExtraPlan = styled.span`
    font-size: 14px;
    font-weight: 300;
`;

const CardTitle = styled.span`
    font-size: 18px;
    font-weight: 500;
`;

const DetailsTitle = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const DetailsDescription = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const ManageButton = styled(Button)`
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const UpgradeButton = styled(Button)`
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    background-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const SubscriptionCard = ({
    subscription,
    secondarySubscription,
    membersAlreadyInPlan,
    isEnterpriseUser,
    isUserPlanAdmin,
}) => {
    const getName = () =>
        subscription &&
        subscription.type &&
        subscription.type.charAt(0).toUpperCase() + subscription.type.slice(1);

    const getRenewalDate = () => {
        if (subscription && subscription.type === "free") return "N/A";
        if (subscription && !subscription.currentPeriodEnd) return "Subscription doesn't expire";
        return (
            <>
                {format(new Date(subscription.currentPeriodEnd * 1000), "MMMM d, u")} (
                {formatDistanceToNow(new Date(subscription.currentPeriodEnd * 1000), {
                    addSuffix: true,
                })}
                )
            </>
        );
    };

    const getTrialEndDate = () => {
        if (!subscription) return null;
        if (subscription.type !== "enterprise") return null;
        if (!subscription.trialEnd) return null;
        return (
            <>
                {format(new Date(subscription.trialEnd * 1000), "MMMM d, u")} (
                {formatDistanceToNow(new Date(subscription.trialEnd * 1000), {
                    addSuffix: true,
                })}
                )
            </>
        );
    };

    const trialEndDate = getTrialEndDate();

    const teamPriceSum = () => {
        if (membersAlreadyInPlan) {
            return ((subscription.price * membersAlreadyInPlan.length) / 100).toFixed(2);
        }
        return null;
    };

    const getPrice = () => {
        if (subscription && subscription.type === "free") return "Forever Free";
        if (subscription && subscription.type === "personal")
            return `${(subscription.price / 100).toFixed(2)}`;
        if (membersAlreadyInPlan && subscription && subscription.type === "team")
            return `${(subscription.price / 100).toFixed(2)}$ x ${
                membersAlreadyInPlan.length
            } Seats = ${teamPriceSum()}$`;
        return null;
    };

    const locationPrefix =
        window.location.hostname === "demo.adadot.com" ? "https://sit.adadot.com" : "";

    const getButtonLabel = () => {
        if (subscription && subscription.type === "free") {
            return "Upgrade Subscription";
        }
        return "Manage Subscription";
    };

    const getAction = async (e) => {
        e.preventDefault();
        if (subscription && subscription.type === "free") {
            const url = await request(
                `${locationPrefix}/api/payments/create-checkout-session/personal?redirect_uri=${getRedirectUrl()}`,
                { method: "POST" }
            );
            window.location.replace(url);
        } else if (subscription && subscription.type === "personal") {
            const url = await request(
                `/api/payments/create-portal-session/personal?redirect_uri=${getRedirectUrl()}`,
                { method: "POST" }
            );
            window.location.replace(url);
        } else if (subscription && subscription.type === "team") {
            const url = await request(
                `${locationPrefix}/api/payments/create-portal-session/team?redirect_uri=${getRedirectUrl()}`,
                { method: "POST", body: JSON.stringify({ lookup_key: "pro_plan" }) }
            );
            window.location.replace(url);
        }
    };

    return (
        <SubscriptionPaper style={{ width: "min(80vw, 350px)" }}>
            <Box p={3}>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <CardTitle>Current Plan</CardTitle>
                        {!isEnterpriseUser && isUserPlanAdmin && (
                            <form onSubmit={getAction}>
                                <input type="hidden" name="lookup_key" value="pro_plan" />
                                {subscription && subscription.type === "free" ? (
                                    <UpgradeButton
                                        id="checkout-and-portal-button"
                                        type="submit"
                                        color="secondary"
                                    >
                                        {getButtonLabel()}
                                    </UpgradeButton>
                                ) : (
                                    <ManageButton
                                        id="checkout-and-portal-button"
                                        type="submit"
                                        color="secondary"
                                    >
                                        {getButtonLabel()}
                                    </ManageButton>
                                )}
                            </form>
                        )}
                    </Stack>
                    <Box pt={2}>
                        <Stack direction="column" spacing={1}>
                            {!subscription ? (
                                <Skeleton variant="text" />
                            ) : (
                                <Plan>
                                    {getName()}
                                    {secondarySubscription && (
                                        <ExtraPlan>+ {secondarySubscription.name}</ExtraPlan>
                                    )}
                                </Plan>
                            )}
                            {trialEndDate ? (
                                <Box pt={1} pb={1}>
                                    <Stack direction="column" spacing={1}>
                                        <DetailsTitle>Trial End Date</DetailsTitle>
                                        {subscription ? (
                                            <>
                                                <DetailsDescription>
                                                    {trialEndDate}
                                                </DetailsDescription>
                                                <Button
                                                    href="mailto:info@adadot.com"
                                                    size="small"
                                                    style={{ marginTop: "25px" }}
                                                >
                                                    Contact us to renew
                                                </Button>
                                            </>
                                        ) : (
                                            <Skeleton variant="text" />
                                        )}
                                    </Stack>
                                </Box>
                            ) : (
                                <Box pt={1} pb={2}>
                                    <Stack direction="column" spacing={1}>
                                        <DetailsTitle>Subscription renewal date</DetailsTitle>
                                        {subscription ? (
                                            <DetailsDescription>
                                                {getRenewalDate()}
                                            </DetailsDescription>
                                        ) : (
                                            <Skeleton variant="text" />
                                        )}
                                    </Stack>
                                </Box>
                            )}
                            {!isEnterpriseUser && isUserPlanAdmin && (
                                <Stack direction="column" spacing={1}>
                                    <DetailsTitle>What you&apos;ll be charged</DetailsTitle>
                                    {subscription ? (
                                        <DetailsDescription>{getPrice()}</DetailsDescription>
                                    ) : (
                                        <Skeleton variant="text" />
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Box>
        </SubscriptionPaper>
    );
};

export default SubscriptionCard;
